// JSX port of the companion's renderHero — the "Your Patient Space" hero with the
// milestone timeline + task-completion status ring. Purely props-driven, read-only.
// The patient-app close button (navigates to withings-bd2://home) is dropped; its
// slot is a spacer so the title stays centered.

import React from 'react';
import { computeRecentCompletion, num, withoutShareInvite } from '@/logic/patientHome';
import { Icon } from './icons';

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Mirrors renderHero's urgency/completion → {theme,title,subtitle,icon} decision tree.
function deriveStatus(tasks, pct) {
  const overdueCount = tasks.filter((t) => t.status === 'overdue').length;
  const urgentCount = tasks.filter((t) => t.priority === 'urgent' && t.status !== 'completed').length;
  const highCount = tasks.filter((t) => t.priority === 'high' && t.status !== 'completed').length;
  const actionableCount = tasks.filter((t) => {
    if (t.status === 'pending' || t.status === 'in_progress' || t.status === 'overdue') return true;
    if (t.status === 'scheduled' && t.available_at && new Date(t.available_at) <= new Date()) return true;
    return false;
  }).length;

  if (overdueCount > 0 || urgentCount > 0) {
    let title;
    let subtitle;
    if (overdueCount > 0 && urgentCount > 0) {
      title = `${overdueCount} Overdue Task${overdueCount > 1 ? 's' : ''}`;
      subtitle = 'You have urgent items that need your attention now.';
    } else if (overdueCount > 0) {
      title = `${overdueCount} Overdue Task${overdueCount > 1 ? 's' : ''}`;
      subtitle = `Please complete ${overdueCount === 1 ? 'this task' : 'these tasks'} as soon as possible.`;
    } else {
      title = `${urgentCount} Urgent Task${urgentCount > 1 ? 's' : ''}`;
      subtitle = 'You have urgent items requiring your attention today.';
    }
    return { theme: 'brown', title, subtitle, icon: 'clock' };
  }
  if (pct == null) {
    return { theme: 'teal', title: 'Getting Started', subtitle: 'You’re all caught up for now. New tasks and lessons will appear as your care plan progresses.', icon: 'clock' };
  }
  if (highCount > 0 && pct >= 80) {
    return { theme: 'purple', title: 'Almost There', subtitle: `Great progress! You still have ${highCount} important task${highCount > 1 ? 's' : ''} to complete.`, icon: 'clock' };
  }
  if (pct >= 80 && actionableCount === 0) {
    return { theme: 'teal', title: 'You’re On Track', subtitle: `${pct}% of your task completion over the last 2 weeks.`, icon: 'check' };
  }
  if (pct >= 80) {
    return { theme: 'teal', title: `${actionableCount} Task${actionableCount > 1 ? 's' : ''} Remaining`, subtitle: `You’re ${pct}% complete — almost there!`, icon: 'badge', badge: actionableCount };
  }
  if (pct >= 50) {
    return { theme: 'purple', title: 'You’re A Little Bit Behind', subtitle: 'Don’t worry, you can catch up by checking your daily tasks today.', icon: 'clock' };
  }
  return { theme: 'brown', title: 'Catch Up with Your Care Plan', subtitle: 'You can catch up by checking your daily tasks today.', icon: 'clock' };
}

function deriveTimeline(carePeriod) {
  const now = new Date();
  let startDate;
  let endDate;
  let progressPct = null;
  if (carePeriod && carePeriod.care_period_start) {
    startDate = new Date(carePeriod.care_period_start);
    endDate = carePeriod.care_period_end
      ? new Date(carePeriod.care_period_end)
      : new Date(startDate.getTime() + 365 * 86400000);
    const prog = carePeriod.progress || {};
    const de = num(prog.days_elapsed);
    const dr = num(prog.days_remaining);
    if (de != null && dr != null && de + dr > 0) {
      progressPct = Math.min(100, Math.max(0, (de / (de + dr)) * 100));
    }
  } else {
    // No care period yet (pre-alignment) — show the first program year, thumb at the start.
    startDate = new Date(now);
    endDate = new Date(startDate.getTime() + 365 * 86400000);
    progressPct = 0;
  }
  const totalDays = Math.max(1, (endDate - startDate) / 86400000);
  const elapsedDays = Math.max(0, (now - startDate) / 86400000);
  if (progressPct == null) progressPct = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));

  const milestones = [
    { pct: 0, label: MONTH_ABBR[startDate.getMonth()] },
    { pct: 25, label: 'Q1' },
    { pct: 50, label: 'Q2' },
    { pct: 75, label: 'Q3' },
    { pct: 100, label: MONTH_ABBR[endDate.getMonth()] },
  ];
  return { progressPct, thumbPct: Math.min(97, Math.max(3, progressPct)), milestones };
}

export default function HeroHeader({ tasks, carePeriod }) {
  const list = withoutShareInvite(tasks);
  const pct = computeRecentCompletion(list);
  const status = deriveStatus(list, pct);
  const { progressPct, thumbPct, milestones } = deriveTimeline(carePeriod);

  const ringR = 36;
  const ringCirc = 2 * Math.PI * ringR;
  const ringOffset = ringCirc * (1 - (pct ?? 0) / 100);

  let statusIcon;
  if (status.icon === 'check') statusIcon = <Icon name="check" size={28} />;
  else if (status.icon === 'badge') statusIcon = <span className="acc-hero__task-badge">{status.badge}</span>;
  else statusIcon = <Icon name="clock" size={28} style={{ color: 'white' }} />;

  return (
    <div className="acc-hero fade-up" data-theme={status.theme}>
      <div className="acc-hero__bar">
        <span className="acc-hero__bar-spacer" aria-hidden="true" />
        <h1 className="acc-hero__title">Your Patient Space</h1>
        <span className="acc-hero__bar-spacer" aria-hidden="true" />
      </div>
      <div>
        <div className="acc-hero__timeline">
          <div className="acc-hero__tl-wrap">
            <div className="acc-hero__tl-labels">
              {milestones.map((m) => {
                const passed = progressPct >= m.pct;
                const isEdge = m.pct === 0 || m.pct === 100;
                const cls = ['acc-hero__tl-label', passed ? 'acc-hero__tl-label--passed' : '', isEdge ? 'acc-hero__tl-label--small' : '']
                  .filter(Boolean).join(' ');
                return <span key={m.pct} className={cls}>{m.label}</span>;
              })}
            </div>
            <div className="acc-hero__tl-bar">
              <div className="acc-hero__tl-fill" style={{ width: `${progressPct}%` }} />
              <div className="acc-hero__tl-thumb" style={{ left: `${thumbPct}%` }} />
            </div>
          </div>
        </div>
        <div className="acc-hero__status">
          <div className="acc-hero__status-ring">
            <svg className="acc-ring-svg" viewBox="0 0 80 80">
              <circle className="acc-hero__ring-track" cx="40" cy="40" r={ringR} />
              <circle className="acc-hero__ring-fill" cx="40" cy="40" r={ringR} strokeDasharray={ringCirc} strokeDashoffset={ringOffset} />
            </svg>
            <div className="acc-hero__status-icon">{statusIcon}</div>
          </div>
          <h2 className="acc-hero__status-title">{status.title}</h2>
          <p className="acc-hero__status-subtitle">{status.subtitle}</p>
        </div>
      </div>
    </div>
  );
}
